import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ExternalLink, Phone, Laptop, Briefcase } from "lucide-react";
import CategoryHero from "./CategoryHero";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from "./NewsTicker";
import SocialLinks, { SOCIAL_FIELDS } from "./SocialLinks";
import FilterChip from "./FilterChip";

const CONSULTING_TYPES: { name: string; icon: typeof Laptop }[] = [
  { name: "IT Consulting", icon: Laptop },
  { name: "Business Consulting", icon: Briefcase },
];
const CONSULTING_TYPE_NAMES = CONSULTING_TYPES.map((c) => c.name);

const CONSULTING_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "description", label: "Description" },
  { key: "category", label: "Type", type: "select", options: CONSULTING_TYPE_NAMES },
  { key: "website", label: "Website URL", type: "url", placeholder: "https://…" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "phone", label: "Contact phone", placeholder: "(469) 343-4228" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

export interface ConsultingEntry {
  name: string;
  description: string;
  category?: string;
  website: string;
  address: string;
  phone?: string;
  image: string;
  hideViewLocation?: boolean;
  hideWebsiteIcon?: boolean;
}

export const CONSULTING_ENTRIES: ConsultingEntry[] = [
  {
    name: "Moazzma Hunain",
    description: "Serial Entrepreneur. Investor. Philanthropist. Business & Mindset Coach.",
    category: "Business Consulting",
    website: "https://www.moazzmahunain.co/",
    address: "",
    image:
      "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783741768108-f5qlbd.png",
    hideViewLocation: true,
    hideWebsiteIcon: true,
  },
];

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop";

function mapsUrl(e: ConsultingEntry) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${e.name}, ${e.address}`)}`;
}

export function ConsultingCard({ entry, index }: { entry: ConsultingEntry; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-md border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="w-full h-[180px] relative overflow-hidden bg-gray-100 flex items-center justify-center">
        <img
          src={entry.image || FALLBACK_IMAGE}
          alt={entry.name}
          className="w-full h-full object-contain"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="p-1.5 pb-0.5 flex-1 flex flex-col justify-between">
        <div>
          <h3 className="text-[10px] font-bold text-[#082f6d] leading-tight truncate">
            {entry.name}
          </h3>
          {entry.description && (
            <p className="text-[9px] text-gray-500 font-medium leading-snug mt-0.5">
              {entry.description}
            </p>
          )}
          {entry.address && (
            <div className="flex items-start gap-0.5 mt-0.5">
              <MapPin className="w-2.5 h-2.5 text-orange-500 shrink-0 mt-0.5" />
              <span className="text-[9px] text-gray-500 font-medium leading-snug truncate">
                {entry.address}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="px-1.5 pb-1.5 pt-0.5 flex flex-col gap-1">
        <SocialLinks item={entry.hideWebsiteIcon ? { ...entry, website: "" } : entry} px={20} />
        {entry.website && (
          <form action={entry.website} method="get" target="_top" className="w-full">
            <button
              type="submit"
              className="w-full flex items-center justify-center gap-0.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200 cursor-pointer"
            >
              <ExternalLink className="w-2.5 h-2.5" /> Visit Website
            </button>
          </form>
        )}
        {!entry.website && entry.phone && (
          <a
            href={`tel:${entry.phone.replace(/[^\d+]/g, "")}`}
            className="w-full flex items-center justify-center gap-0.5 bg-[#51ba4f] hover:bg-[#459f43] text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200 cursor-pointer"
          >
            <Phone className="w-2.5 h-2.5" /> Call
          </a>
        )}
        {entry.address && !entry.hideViewLocation && (
          <a
            href={mapsUrl(entry)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-0.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200"
          >
            <ExternalLink className="w-2.5 h-2.5" /> View Location
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function Consulting() {
  const [query, setQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const mc = useManagedCollection<any>("consulting", CONSULTING_ENTRIES, (e, i) => slugify(e.name || String(i)));

  const filtered = mc.items.filter(
    (e) =>
      (query.trim() === "" ||
        e.name.toLowerCase().includes(query.trim().toLowerCase())) &&
      (!selectedType || e.category === selectedType)
  );

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="Consulting"
        placeholder="Search IT consultants & firms…"
        backgroundImage="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1440&h=400&fit=crop"
        query={query}
        setQuery={setQuery}
      />
      <NewsTicker />

      {/* ── Filter Bar ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 pt-6 pb-4">
          <p className="text-[11px] font-bold text-gray-400 uppercase mb-2.5" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '0.12em' }}>
            Explore by Type
          </p>
          <div className="flex gap-2 overflow-x-auto filter-scroll pb-1 flex-wrap">
            {CONSULTING_TYPES.map((t) => {
              const Icon = t.icon;
              const active = selectedType === t.name;
              return (
                <FilterChip
                  key={t.name}
                  active={active}
                  onClick={() => setSelectedType(active ? null : t.name)}
                  color="#082f6d"
                >
                  <span className="flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5" />
                    {t.name}
                  </span>
                </FilterChip>
              );
            })}
            {selectedType && (
              <button
                onClick={() => setSelectedType(null)}
                className="rounded-full border border-dashed border-gray-300 text-gray-500 text-[12.5px] font-medium px-3.5 py-2 whitespace-nowrap hover:text-[#082f6d] hover:border-[#082f6d] transition-colors"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <section
        className="bg-gray-50/40 py-8"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-1">
              IT Consultants & Firms
            </h2>
            <p className="text-[13px] text-gray-500 mb-5">
              Find Indian and South Asian IT consulting professionals, software firms, and tech consultants serving the desi community in Dallas–Fort Worth.
            </p>
            <AdminAddBar mc={mc} fields={CONSULTING_FIELDS} label="Add Listing" />
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                {filtered.map((e, i) => (
                  <AdminItem key={e._key} mc={mc} item={e} fields={CONSULTING_FIELDS} title="Listing">
                    <ConsultingCard entry={e as any} index={i} />
                  </AdminItem>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-[13px] text-gray-500">
                No consulting listings yet.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
